<script setup lang="ts">
import AppNavigation from '@/components/app-shell/AppNavigation.vue'
import AppProfileMenu from '@/components/app-shell/AppProfileMenu.vue'
import AppSidebar from '@/components/app-shell/AppSidebar.vue'
import Toaster from '@/components/Toaster.vue'
import { router } from '@inertiajs/vue3'
import { useHead } from '@unhead/vue'
import { onKeyStroke, useLocalStorage, useMediaQuery } from '@vueuse/core'
import { computed, nextTick, onUnmounted, provide, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const contextConsumers = ref(0)
provide('appContext', {
  register: () => contextConsumers.value++,
  unregister: () => contextConsumers.value--,
})
const contextBarEnabled = false
const hasContext = computed(() => contextBarEnabled && contextConsumers.value > 0)

useHead({
  bodyAttrs: {
    class: 'app',
  },
  htmlAttrs: {
    class: 'app',
  },
})

const contextOpen = useLocalStorage('finesthub.context-open', true)

const isDesktop = useMediaQuery('(min-width: 64rem)')

const isWide = useMediaQuery('(min-width: 80rem)')

const showRail = computed(() => isDesktop.value && (!isWide.value || !contextOpen.value))

const mobileVisible = ref(false)

const mobileContextVisible = ref(false)

onKeyStroke('Escape', () => (mobileContextVisible.value = false))

const contextPanel = ref<HTMLElement | null>(null)

watch(mobileContextVisible, async (visible) => {
  if (!visible) {
    return
  }

  await nextTick()
  contextPanel.value?.querySelector<HTMLElement>('button')?.focus()
})

const removeSuccessListener = router.on('success', () => {
  mobileVisible.value = false
  mobileContextVisible.value = false
})

onUnmounted(() => {
  removeSuccessListener()
})
</script>

<template>
  <div>
    <Toaster />
    <ConfirmDialog />

    <div class="min-h-screen" :class="showRail ? '[--ctx-space:var(--ctx-rail)]' : '[--ctx-space:var(--ctx-w)]'">
      <div class="flex min-h-screen">
        <aside>
          <Drawer
            v-model:visible="mobileVisible"
            class="lg:hidden"
            position="left"
            :aria-label="t('sidebar_label')"
            :dt="{ content: { padding: '0' } }">
            <AppSidebar>
              <AppNavigation />
              <template #footer>
                <AppProfileMenu />
              </template>
            </AppSidebar>
          </Drawer>

          <div
            class="bg-surface-0 border-surface-200 fixed top-0 left-0 z-20 hidden h-screen w-(--nav-w) overflow-hidden border-r lg:block">
            <AppSidebar>
              <AppNavigation />
              <template #footer>
                <AppProfileMenu />
              </template>
            </AppSidebar>
          </div>
        </aside>

        <div
          class="flex min-w-0 flex-1 flex-col gap-6 transition-[padding] duration-200 ease-out motion-reduce:transition-none lg:pl-(--nav-w)"
          :class="{ 'lg:pr-(--ctx-space)': hasContext }">
          <header
            class="bg-surface-0 border-surface-200 sticky top-0 z-20 flex h-16 items-center border-b px-4 lg:hidden">
            <Button
              icon="pi pi-bars"
              size="large"
              severity="secondary"
              rounded
              :aria-label="t('open_menu')"
              @click="mobileVisible = true" />
            <Link href="/" class="absolute left-1/2 inline-flex -translate-x-1/2">
              <img src="@assets/images/logo.svg" :alt="t('brand')" class="h-8" />
            </Link>
            <Button
              v-if="hasContext"
              icon="pi pi-sparkles"
              size="large"
              severity="secondary"
              rounded
              class="ml-auto"
              :aria-label="t('expand_assistant')"
              :aria-expanded="mobileContextVisible"
              aria-controls="app-context-target"
              @click="mobileContextVisible = true" />
          </header>

          <main class="mx-auto flex w-full max-w-384 flex-1 flex-col p-4 md:p-6 lg:p-8">
            <slot />
          </main>
        </div>

        <div
          v-show="hasContext && mobileContextVisible"
          class="fixed inset-0 z-20 bg-black/40 lg:hidden"
          @click="mobileContextVisible = false" />

        <aside
          v-show="hasContext"
          ref="contextPanel"
          :role="isDesktop ? undefined : 'dialog'"
          :aria-modal="!isDesktop && mobileContextVisible ? 'true' : undefined"
          class="bg-surface-0 border-surface-200 fixed top-0 right-0 z-30 flex h-screen w-(--ctx-w) flex-col border-l shadow-2xl transition-[transform,width] duration-200 ease-out motion-reduce:transition-none lg:z-10 lg:w-(--ctx-space) lg:translate-x-0 lg:shadow-none"
          :class="mobileContextVisible ? 'translate-x-0' : 'translate-x-full'"
          :aria-label="t('assistant_label')">
          <div
            class="border-surface-200 flex h-16 shrink-0 items-center border-b px-2"
            :class="showRail ? 'justify-center' : 'justify-between'">
            <span v-if="!showRail" class="text-surface-500 px-2 text-xs font-medium tracking-wide uppercase">
              {{ t('assistant_label') }}
            </span>
            <Button
              v-if="!isDesktop"
              icon="pi pi-times"
              :aria-label="t('close_assistant')"
              severity="secondary"
              text
              rounded
              @click="mobileContextVisible = false" />
            <Button
              v-if="isWide"
              :icon="contextOpen ? 'pi pi-angle-double-right' : 'pi pi-angle-double-left'"
              :aria-label="contextOpen ? t('collapse_assistant') : t('expand_assistant')"
              :aria-expanded="contextOpen"
              aria-controls="app-context-target"
              severity="secondary"
              text
              rounded
              @click="contextOpen = !contextOpen" />
          </div>
          <div v-show="!showRail" id="app-context-target" class="fh-ctx min-h-0 flex-1" />
          <div
            v-show="showRail"
            id="app-context-rail-target"
            class="flex min-h-0 flex-1 flex-col items-center gap-3 overflow-y-auto px-1 py-4" />
        </aside>
      </div>
    </div>
  </div>
</template>

<i18n>
{
  "de": {
    "open_menu": "Menü öffnen",
    "sidebar_label": "Hauptnavigation",
    "assistant_label": "Assistent",
    "collapse_assistant": "Assistent einklappen",
    "close_assistant": "Assistent schließen",
    "expand_assistant": "Assistent öffnen",
    "brand": "FinEstHub"
  },
  "en": {
    "open_menu": "Open menu",
    "sidebar_label": "Main navigation",
    "assistant_label": "Assistant",
    "collapse_assistant": "Collapse assistant",
    "close_assistant": "Close assistant",
    "expand_assistant": "Open assistant",
    "brand": "FinEstHub"
  }
}
</i18n>
